import styled, { ThemedStyledProps } from 'styled-components';
import { Chip as BaseChip, ChipProps } from '@mui/material';
import { Theme } from '@mui/material/styles/createTheme';
import { ReactComponent as CloseIcon } from '../../assets/close-2.svg';
import SvgIcon from '../SvgIcon';
import { componentToken, declaration } from '../../style/utils';

export type ChipColor = ChipProps['color'] | 'idle' | 'merged' | 'primary';

export type Props = Omit<ChipProps, 'color'> & {
  size?: 'small' | 'medium';
  bold?: boolean;
  customVariant?: 'dashed' | 'crossed' | 'rounded';
  overflow?: 'visible' | 'hidden';
};

type InnerProps = Props & { $color?: ChipColor };

const StyledChip = styled(BaseChip).attrs((props: InnerProps) => ({
  'aria-label': props['aria-label'] || 'chip',
}))<InnerProps>`
  border-radius: ${({ variant, customVariant }) =>
    variant === 'outlined' || customVariant === 'rounded'
      ? componentToken('chip.roundedRadius')
      : componentToken('chip.radius')};
  font-size: ${componentToken('chip.fontSize')};
  font-weight: ${({ bold, theme }) =>
    bold ? theme.typography.fontWeightBold : componentToken('chip.fontWeight')};
  padding: ${({ onDelete, variant, customVariant }) =>
    variant === 'outlined' || customVariant === 'rounded'
      ? onDelete
        ? componentToken('chip.roundedDeletablePadding')
        : componentToken('chip.roundedPadding')
      : onDelete
      ? componentToken('chip.deletablePadding')
      : componentToken('chip.padding')};
  height: fit-content;
  gap: ${componentToken('chip.gap')};
  overflow: hidden;

  & .MuiChip-label {
    padding-top: 0px;
    padding-bottom: 0;
    padding-left: ${({ variant, customVariant }) =>
      variant === 'outlined' || customVariant === 'rounded'
        ? componentToken('chip.roundedLabelPaddingLeft')
        : '0px'};
    overflow: ${({ overflow }) => overflow || 'hidden'};
    padding-right: ${({ onDelete }) =>
      !onDelete ? '0' : componentToken('chip.deletableLabelPaddingRight')};
  }

  .MuiChip-icon {
    margin: 0;
  }

  .MuiChip-deleteIcon {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: -10px;
      bottom: -10px;
      left: -6px;
      right: -15px;
      cursor: pointer;
      border-radius: ${componentToken('chip.deleteIconHitAreaRadius')};
      transition: background-color 0.2s ease;
    }

    &:hover::after {
      background-color: ${componentToken('chip.deleteIconHoverBg')};
    }
  }

  ${({ customVariant, theme }) =>
    customVariant === 'dashed'
      ? `
    border: 1px dashed ${theme.tokens.components.chip.dashedBorder};
    `
      : customVariant === 'crossed'
      ? `
        border: 1px dashed ${theme.tokens.components.chip.dashedBorder};
        text-decoration: line-through;
    `
      : ``};

  ${({
    $color,
    theme,
    variant,
    customVariant,
  }: ThemedStyledProps<InnerProps, Theme>) => {
    const { colors } = theme.tokens.components.chip;
    const chipColor =
      $color === 'error' ||
      $color === 'warning' ||
      $color === 'success' ||
      $color === 'idle' ||
      $color === 'merged' ||
      $color === 'primary'
        ? colors[$color]
        : undefined;
    if (chipColor) {
      return `
    color: ${chipColor.fg};
    background: ${chipColor.bg};
    `;
    }
    if ($color === 'default' || !$color) {
      return `
    color: ${colors.default.fg};
    background: ${
      variant === 'outlined' || customVariant === 'rounded'
        ? colors.default.bg
        : customVariant
        ? colors.default.customVariantBg
        : colors.default.bg
    };
    `;
    }
    return ``;
  }};

  ${({ size, theme }) => {
    const chip = theme.tokens.components.chip;
    return size === 'small'
      ? `
      font-size: ${chip.smallFontSize};
      ${declaration('line-height', chip.smallLineHeight)}
      min-width: 22px;
      height: 18px;
    `
      : size === 'medium'
      ? `
      font-size: ${chip.mediumFontSize};
      ${declaration('line-height', chip.mediumLineHeight)}
      height: 24px;
      `
      : `height: 28px; ${declaration('line-height', chip.lineHeight)}`;
  }};
`;

const Chip = ({ color, ...props }: Props & { color?: ChipColor }) => (
  <StyledChip
    $color={color}
    deleteIcon={
      <SvgIcon width={10}>
        <CloseIcon />
      </SvgIcon>
    }
    {...props}
  />
);

export default Chip;
